class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.pendingSubmitEvent = null;
    this.debouncedOnSubmit = debounce(() => this.flushPendingSubmit(), 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', (event) => {
      this.pendingSubmitEvent = event;
      // An older response must not erase a choice still awaiting debounce.
      FacetFiltersForm.requestId++;
      this.debouncedOnSubmit();
    });

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      document.querySelectorAll('facet-filters-form').forEach(form => { form.pendingSubmitEvent = null; });
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const requestId = ++FacetFiltersForm.requestId;
    const sections = FacetFiltersForm.getSections();
    const updateEvent = FacetFiltersForm.startUpdateEvent(searchParams);
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, updateEvent, requestId)
        : FacetFiltersForm.renderSectionFromFetch(url, event, updateEvent, requestId);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static startUpdateEvent(searchParams) {
    const { SearchUpdateEvent, CollectionUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent && !CollectionUpdateEvent) return;

    const facetsForm = document.querySelector('facet-filters-form');
    const facetsContainer = document.querySelector('.facets-container');
    const collectionId = facetsContainer?.dataset.collectionId || null;
    const collectionHandle = facetsContainer?.dataset.collectionHandle || '';
    const currentCount = parseInt(document.getElementById('ProductCount')?.dataset.productCount) || 0;
    const urlSearchParams = new URLSearchParams(searchParams);
    const isSearchPage = facetsContainer?.dataset.template === 'search';
    const isCollectionPage = facetsContainer?.dataset.template === 'collection';
    const dispatchTarget = facetsForm || document;

    let deferred;

    if (isSearchPage) {
      deferred = SearchUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new SearchUpdateEvent({
          search: {
            query: urlSearchParams.get('q') || '',
            productFilters: SearchUpdateEvent.parseProductFilters(urlSearchParams),
            sortKey: SearchUpdateEvent.getSortKey(urlSearchParams),
          },
          promise: deferred.promise,
        }),
      );
    } else if (isCollectionPage) {
      // collectionId is null for auto-generated collections (/collections/all,
      // /collections/vendors/<x>, etc.) — the SSE schema accepts that.
      deferred = CollectionUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new CollectionUpdateEvent({
          collection: { id: collectionId, handle: collectionHandle, productsCount: currentCount },
          productFilters: CollectionUpdateEvent.parseProductFilters(urlSearchParams),
          sortKey: CollectionUpdateEvent.getSortKey(urlSearchParams),
          promise: deferred.promise,
        }),
      );
    }

    if (!deferred) return;

    return {
      resolve: (filteredCount) => {
        deferred.resolve({ [isSearchPage ? 'totalCount' : 'productsCount']: filteredCount });
      },
      reject: deferred.reject,
    };
  }

  static renderSectionFromFetch(url, event, updateEvent, requestId) {
    fetch(url)
      .then((response) => response.text())
      .then((html) => {
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderSection(html, event, updateEvent, requestId);
      })
      .catch((error) => {
        console.error(error);
        updateEvent?.reject(error);
      });
  }

  static renderSectionFromCache(filterDataUrl, event, updateEvent, requestId) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderSection(html, event, updateEvent, requestId);
  }

  static renderSection(html, event, updateEvent, requestId = FacetFiltersForm.requestId) {
    if (requestId !== FacetFiltersForm.requestId) {
      // Resolve the original analytics event with its own result count, but
      // never repaint an older selection over the shopper's latest choice.
      const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount');
      updateEvent?.resolve(parseInt(count?.dataset.productCount) || 0);
      return;
    }
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html, updateEvent);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html, updateEvent) {
    const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
    const sourceCount = parsedHtml.getElementById('ProductCount');
    const count = sourceCount.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.dataset.productCount = sourceCount.dataset.productCount || '';
    container.dataset.totalCount = sourceCount.dataset.totalCount || '';
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));

    updateEvent?.resolve(parseInt(sourceCount.dataset.productCount) || 0);
  }

  static renderFilters(html, event) {
    // Capture before replacing controls, but respect a shopper who has already
    // pressed Apply or moved focus elsewhere while the request was pending.
    const focusOrigin = document.activeElement;
    const shortcutValue = focusOrigin?.matches('[data-bd-deal-filter]') ? focusOrigin.dataset.categoryValue : null;
    const restoreDealsFocus = focusOrigin?.matches('[data-bd-deals-shortcut]');
    const clearFacetId = focusOrigin?.matches('[data-bd-inline-filter] .mobile-facets__clear') ? focusOrigin.closest('.js-filter').id : null;
    const originatingFacet = event?.target.closest('.js-filter');
    const restoreFacetFocus = originatingFacet?.contains(document.activeElement);
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);

        const isTextInput = event.target.getAttribute('type') === 'text';

        const drawer = newFacetDetailsElement.closest('menu-drawer');
        const facetIsOpen = newFacetDetailsElement.hasAttribute('open');
        const drawerIsOpen = !drawer || drawer.querySelector('details')?.hasAttribute('open');

        if (!isTextInput && restoreFacetFocus && facetIsOpen && drawerIsOpen) {
          // Try to return focus to the same checkbox the user just toggled,
          // re-selecting it from the freshly rendered HTML by its id.
          const originatingInputId = event.target.id;
          const matchingInput = originatingInputId
            ? newFacetDetailsElement.querySelector(`#${CSS.escape(originatingInputId)}`)
            : null;

          if (matchingInput) {
            matchingInput.focus();
          } else {
            // Fallback to summary/close button if the checkbox can't be found
            const fallbackSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
              ? `.mobile-facets__summary`
              : `.facets__summary`;
            const fallbackElement = newFacetDetailsElement.querySelector(fallbackSelector);
            if (fallbackElement) fallbackElement.focus();
          }
        }
      }
    }

    // Run after every replacement, including the active facet's counts/summary.
    initializeDetails(document.querySelector('.facets-container') || document);
    document.getElementById('FacetFiltersFormMobile')?.closest('menu-drawer')?.bindEvents();
    if (!focusOrigin?.isConnected && document.activeElement === document.body) {
      if (restoreDealsFocus) {
        // If the last matching deal disappeared, keep keyboard focus in the rail.
        (document.querySelector('[data-bd-deals-shortcut]') ||
          document.querySelector('.bd-deal-toolbar__row .mobile-facets__open-wrapper'))?.focus({preventScroll: true});
      } else if (shortcutValue !== null) {
        const links = [...document.querySelectorAll('[data-bd-deal-filter]')];
        (links.find(link => link.dataset.categoryValue === shortcutValue) || links[0])?.focus({preventScroll: true});
      } else if (clearFacetId) {
        document.getElementById(clearFacetId)?.querySelector('summary')?.focus({preventScroll: true});
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting', '#BDDealsShortcut', '#BDDealCategories'];

    mobileElementSelectors.forEach((selector) => {
      const source = html.querySelector(selector), target = document.querySelector(selector);
      if (!source || !target) return;
      target.innerHTML = source.innerHTML;
    });
    // This persistent footer sits outside .js-filter. Keep its Clear All URL
    // current without replacing Apply or disturbing keyboard focus.
    const clearSelector = '[data-bd-inline-filters] .mobile-facets__main > .mobile-facets__footer .mobile-facets__clear';
    const sourceClear = html.querySelector(clearSelector), targetClear = document.querySelector(clearSelector);
    if (sourceClear && targetClear) targetClear.setAttribute('href', sourceClear.getAttribute('href'));
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const sourceSummary = source.querySelector('.mobile-facets__summary');
    const targetSummary = target.querySelector('.mobile-facets__summary');
    if (sourceSummary && targetSummary) targetSummary.innerHTML = sourceSummary.innerHTML;
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    form.querySelectorAll('price-range').forEach(range => range.normalizeFormData(formData));
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  flushPendingSubmit() {
    const event = this.pendingSubmitEvent;
    if (!event) return;
    this.pendingSubmitEvent = null;
    if (event.target.isConnected === false) return;
    this.onSubmitHandler(event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    // Clear/Remove All supersedes a thumb movement still awaiting debounce.
    document.querySelectorAll('facet-filters-form').forEach(form => { form.pendingSubmitEvent = null; });
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.requestId = 0;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    // Only the named text fields belong in Shopify's submitted price query.
    [this.minInput, this.maxInput] = this.querySelectorAll('.field__input');
    this.slider = this.querySelector('[data-price-slider]');
    this.minRange = this.querySelector('[data-price-min]');
    this.maxRange = this.querySelector('[data-price-max]');
    this.rangeMax = Number(this.slider?.dataset.max || this.maxInput.dataset.max);
    this.decimalSeparator = this.slider?.dataset.decimalSeparator || '.';
    this.formatter = new Intl.NumberFormat(document.documentElement.lang || 'en', {
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    });
    [this.minInput, this.maxInput].forEach(input => {
      input.addEventListener('input', () => this.syncFromFields());
      input.addEventListener('change', () => {
        this.adjustToValidValues(input);
        this.syncFromFields();
      });
      input.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    [this.minRange, this.maxRange].filter(Boolean).forEach(input => {
      input.disabled = this.rangeMax <= 0;
      input.addEventListener('input', () => this.onSliderInput(input));
    });
    this.syncFromFields();
  }

  parsePrice(value) {
    const text = String(value).trim().replace(/[\s']/g, '');
    if (!text) return null;
    // Native query values always use a dot; also accept pasted local money.
    const decimal = text.includes('.') && text.includes(',')
      ? (text.lastIndexOf('.') > text.lastIndexOf(',') ? '.' : ',')
      : this.decimalSeparator;
    const normalized = decimal === ',' && text.includes(',')
      ? text.replaceAll('.', '').replace(',', '.') : text.replaceAll(',', '');
    const price = Number(normalized);
    return Number.isFinite(price) ? price : null;
  }

  clamp(value, min = 0, max = this.rangeMax) {
    return Math.round(Math.min(max, Math.max(min, value)) * 100) / 100;
  }

  selectedBounds() {
    const max = this.clamp(this.parsePrice(this.maxInput.value) ?? this.rangeMax);
    const min = this.clamp(this.parsePrice(this.minInput.value) ?? 0, 0, max);
    return { min, max };
  }

  syncFromFields() {
    const { min, max } = this.selectedBounds();
    this.minInput.dataset.max = String(max);
    this.maxInput.dataset.min = String(min);
    if (!this.slider) return;
    this.minRange.value = String(min);
    this.maxRange.value = String(max);
    this.updateSlider(min, max);
  }

  normalizeFormData(formData) {
    const { min, max } = this.selectedBounds();
    formData.set(this.minInput.name, this.parsePrice(this.minInput.value) === null ? '' : String(min));
    formData.set(this.maxInput.name, this.parsePrice(this.maxInput.value) === null ? '' : String(max));
  }

  onSliderInput(input) {
    const min = input === this.minRange
      ? this.clamp(Number(input.value), 0, Number(this.maxRange.value)) : Number(this.minRange.value);
    const max = input === this.maxRange
      ? this.clamp(Number(input.value), min) : Number(this.maxRange.value);
    this.minRange.value = String(min);
    this.maxRange.value = String(max);
    // Run before the same native input event bubbles to the facet form. Blank
    // endpoints preserve the existing unbounded/Clear behavior without extras.
    this.minInput.value = min === 0 ? '' : String(min);
    this.maxInput.value = max === this.rangeMax ? '' : String(max);
    this.minInput.dataset.max = String(max);
    this.maxInput.dataset.min = String(min);
    this.updateSlider(min, max);
  }

  updateSlider(min, max) {
    const start = this.rangeMax > 0 ? min / this.rangeMax * 100 : 0;
    const end = this.rangeMax > 0 ? max / this.rangeMax * 100 : 100;
    this.slider.style.setProperty('--price-start', `${start}%`);
    this.slider.style.setProperty('--price-end', `${end}%`);
    // When both handles meet at the upper edge, keep the lower one reachable.
    this.minRange.style.zIndex = min === this.rangeMax ? '3' : '1';
    const currency = this.slider.dataset.currency;
    const minText = `${this.formatter.format(min)} ${currency}`;
    const maxText = `${this.formatter.format(max)} ${currency}`;
    this.querySelector('[data-price-min-value]').textContent = minText;
    this.querySelector('[data-price-max-value]').textContent = maxText;
    this.minRange.setAttribute('aria-valuetext', minText);
    this.maxRange.setAttribute('aria-valuetext', maxText);
    this.minRange.setAttribute('aria-valuemax', String(max));
    this.maxRange.setAttribute('aria-valuemin', String(min));
  }

  onKeyDown(event) {
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|Home|End|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  adjustToValidValues(input) {
    const { min, max } = this.selectedBounds();
    this.minInput.value = this.parsePrice(this.minInput.value) === null ? '' : String(min);
    this.maxInput.value = this.parsePrice(this.maxInput.value) === null ? '' : String(max);
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    if (facetLink.hasAttribute('data-bd-deal-filter')) facetLink.setAttribute('aria-pressed', facetLink.dataset.active === 'true');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    // Keep real link navigation when filtering/sorting is disabled in the editor.
    if (!form) {
      if (event.type === 'keyup' && event.code.toUpperCase() === 'SPACE') window.location.assign(event.currentTarget.href);
      return;
    }
    event.preventDefault();
    const shortcut = event.currentTarget;
    if (shortcut.hasAttribute('data-bd-deals-shortcut')) {
      const param = shortcut.closest('[data-filter-param]')?.dataset.filterParam;
      const value = shortcut.dataset.dealValue;
      if (param && value) {
        // Apply can close the drawer before its input debounce has fired.
        // Flush it first; its queued timer then finds no pending event to submit.
        document.querySelectorAll('facet-filters-form').forEach(facetForm => facetForm.flushPendingSubmit());
        // Use the latest submitted query if clicks overlap an AJAX response.
        const params = new URLSearchParams(FacetFiltersForm.searchParamsPrev);
        const wasSelected = params.getAll(param).includes(value);
        params.delete(param); params.delete('page');
        if (!wasSelected) params.append(param, value);
        form.onSubmitForm(params.toString(), event);
        return;
      }
    }
    if (shortcut.hasAttribute('data-bd-deal-filter')) {
      const param = shortcut.closest('[data-filter-param]')?.dataset.filterParam;
      if (param) {
        // A second click can arrive before the first HTML response. Merge with
        // the latest submitted query, never with an out-of-date rendered URL.
        const params = new URLSearchParams(FacetFiltersForm.searchParamsPrev);
        const values = new Set(params.getAll(param)), value = shortcut.dataset.categoryValue;
        if (!value) values.clear();
        else if (values.has(value)) values.delete(value);
        else values.add(value);
        params.delete(param); params.delete('page');
        values.forEach(selected => params.append(param, selected));
        form.onSubmitForm(params.toString(), event);
        return;
      }
    }
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
